import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigation, useRoute, useFocusEffect } from "@react-navigation/native";
import {
  View,
  StyleSheet,
  ScrollView,
  Platform,
  KeyboardAvoidingView,
  TouchableOpacity,
} from "react-native";
import { useTranslation } from "react-i18next";
import { Text, TextInput } from "react-native-paper";
import { useThemeSettings } from "../../ThemeProvider";
import getGlobalStyles from "../../globalStyles";
import { ArticleCard } from "../news-feed/components/article-card";
import ShareWrapper from "../../components/ShareWrapper";
import { ARTICLE_HEIGHT} from "../../shared/constants/dimension";
import TagInputField from "./components/TagInputField";
import { previewArticles, saveCollection, getCollectionById } from "../../services/apiService";
import { useRecommendations } from "../../services/tanStackService";
import { useCollectionStore } from "../../store/collectionStore";
import { useUserStore } from "../../store/userStore";
import { CustomButton, ButtonVariant } from "../../components/CustomButton";
import { Article } from "../../shared/types/DataTypes";
import ThinkOLogo from "../../components/ThinkoLogo";
import { CollectionBubble } from "../../components/CollectionBubble";
import { CustomIcon } from "../../components/CustomIcon";
import { TrackedTouchable } from "../../analytics/components";
import { useKeyboardHeight } from "../../hooks/useKeyboardHeight";
import { LinearGradient } from "expo-linear-gradient";
import { queryClient } from "../../services/tanStackClient";
import { trackInteraction } from "../../analytics/analyticsUtil";
import { ACTION_TYPE, ANALYTICS_EVENT } from "../../analytics/types";
import { ROUTES } from "../../shared/constants/routes";
import { MODAL_MAX_WIDTH } from "../../shared/constants/dimension";

const OPTIONAL_FILTERS = ["Global", "National", "Regional", "State"];
const EditCollectionScreen: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const navigation = useNavigation<any>();
  const routeNav = useRoute<any>();
  const { t, i18n } = useTranslation();
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isCollectionNameInputFocused, setCollectionNameIsInputFocused] = useState(false);
  const { theme } = useThemeSettings();
  const gStyles = useMemo(() => getGlobalStyles(theme), [theme]);

  const { setReload, editCollection, setEditCollection } = useCollectionStore();
  const { user } = useUserStore();
  const [collectionName, setCollectionName] = useState(editCollection?.title || "");
  const [orTags, setOrTags] = useState<any[]>(editCollection?.orTags ?? editCollection?.SeoTags ?? []);
  const [andTag, setAndTag] = useState<any | null>(editCollection?.andTag ?? null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [articles, setArticles] = useState<Article[]>([]);
  const { data: recommendedCollections = [] } = useRecommendations(4);
  const keyboardHeight = useKeyboardHeight();
  const [isSaving, setIsSaving] = useState(false);
  // Reset editCollection on unmount
  useEffect(() => () => setEditCollection(null), [setEditCollection]);
    useEffect(() => {
    setCollectionName(editCollection?.title || "");
    setOrTags(editCollection?.orTags ?? editCollection?.SeoTags ?? []);
    setAndTag(editCollection?.AndTag ?? null);
    setArticles([]);
  }, [editCollection]);
  useFocusEffect(
    useCallback(() => {
      const current = useCollectionStore.getState().editCollection;
      setCollectionName(current?.title || "");
      setOrTags(current?.orTags ?? current?.SeoTags ?? []);
      setAndTag(current?.AndTag ?? null);
      setArticles([]);
    }, [])
  );

  useEffect(() => {
    const id = routeNav.params?.collectionId;
    if (id && !useCollectionStore.getState().editCollection?.id) {
      getCollectionById(id).then((c) => c && setEditCollection(c));
    }
  }, [routeNav.params?.collectionId, setEditCollection]);

  // Preview articles
  const onPreview = useCallback(async () => {
    if (!orTags.length) {
      setArticles([]);
      return;
    }
    setIsProcessing(true);
    try {
        const preViewData = await previewArticles(orTags.map((t) => t.id), andTag?.id);

      setArticles(preViewData?.articles ?? []);
    } finally {
      setIsProcessing(false);
    }
  }, [orTags, andTag]);

  useEffect(() => {
    onPreview();
  }, [onPreview]);

  // Save edited collection
  const saveEditedCollection = useCallback(async () => {
      setIsSaving(true);
      try {
        const isCreate = !editCollection?.id;
        const collectionId = editCollection?.id;
        const userId = user?.firebaseUser?.uid;
        await saveCollection({
          id: editCollection?.id,
          title: collectionName,
          orTags: orTags.map((t) => t.id),
          andTag: andTag?.id,
        });
        if (isCreate) {
          trackInteraction({
            event: ANALYTICS_EVENT.INTERACTION,
            action: ACTION_TYPE.CLICK,
            screen_name: ROUTES.CREATE_COLLECTION,
            component: "SmartCollectionSave",
            context: {
              flow: "smart_collection",
              cta: "SMART_COLLECTION_CREATE",
              selected_tag_count: orTags.length,
            },
          });
        }
        if (collectionId && userId) {
          queryClient.refetchQueries({
            queryKey: ["articles", i18n.language, collectionId, userId],
            exact: true,
            type: "all",
          });
        }
        setEditCollection(null);
        setReload(true);
        setCollectionName("");
        setOrTags([]);
        setAndTag(null);
        setArticles([]);
        onClose();
      } finally {
        setIsSaving(false);
      }
    }, [collectionName, orTags, andTag, editCollection,i18n.language,user?.firebaseUser?.uid, setEditCollection, setReload, onClose]);

  // Handle recommended collection click
  const handleCollectionCirclePress = useCallback((c: any) => {
    const tags = c.SeoTags || c.tags || [];
    const uniqueTags = tags.filter(
      (tag, index, self) => index === self.findIndex((t) => String(t.id) === String(tag.id))
    );
    setCollectionName(c.title);
    setOrTags(uniqueTags);
    setAndTag(c.AndTag ?? null);
  }, []);

  // Memoize recommended bubbles
  const recommendedBubbles = useMemo(() => (
    recommendedCollections.map((c, index) => (
      <CollectionBubble
        key={`recommended${c.id}${index}`}
        collection={c}
        index={index}
        isDisabled={orTags.some((t) => (c.SeoTags || c.tags || []).some((s: any) => s.id === t.id))}
        onBubblePress={handleCollectionCirclePress}
      />
    ))
  ), [recommendedCollections, orTags, handleCollectionCirclePress]);

  // Memoize article cards
  const articleCards = useMemo(() => (
    articles.map((article) => (
      <View key={article.id} style={[styles.gridItem, articles.length === 1 && { width: "100%" }]}>
        <ShareWrapper>
          {(viewRef: any) => (
            <ArticleCard ref={viewRef} fromRadio={false} key={article.id} article={article} height={ARTICLE_HEIGHT} isDisable={true}/>
          )}
        </ShareWrapper>
      </View>
    ))
  ), [articles]);

   const selectedOptionalFilter = useMemo(
    () => String(andTag?.name ?? andTag?.id ?? "").toLowerCase(),
    [andTag],
  );

  const renderOptionalFilters = () => (
    <View>
      <Text variant="bodyMedium" style={[styles.optionalFilterTitle, { color: theme.colors.primary }]}>
        {t("screens.collections.optionalFilter")}
      </Text>
      <View style={styles.optionalFilterGrid}>
        {OPTIONAL_FILTERS.map((filter) => {
          const filterValue = filter.toLowerCase();
          const selected =
            selectedOptionalFilter === filterValue ||
            selectedOptionalFilter.startsWith(`${filterValue}_`);

          return (
            <TouchableOpacity
              key={filter}
              activeOpacity={0.85}
              onPress={() => setAndTag(selected ? null : { id: filterValue, name: filter })}
              style={[
                styles.optionalChip,
                { borderColor: theme.colors.primary, backgroundColor: theme.colors.surfaceBg },
                selected && [
                  styles.optionalChipSelected,
                  { backgroundColor: theme.colors.primary },
                ],
              ]}
            >
              <Text
                style={[
                  styles.optionalChipText,
                  { color: selected ? theme.colors.surfaceBg : theme.colors.primary },
                ]}
              >
                {filter}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );

  const scrollContentStyle = useMemo(() => [
    styles.container,
    { paddingBottom: keyboardHeight || ((isInputFocused || isCollectionNameInputFocused) && Platform.OS !== 'web') ? 300 : 0 },
  ], [keyboardHeight, isInputFocused, isCollectionNameInputFocused]);

  const editCollectionView = () => {
    const children = <View style={styles.bottomRow}>
      <TextInput
        mode="outlined"
        value={collectionName}
        onFocus={() => {
          setTimeout(() => {
            setCollectionNameIsInputFocused(true);
          }, 100);
        }}
        onBlur={() => {
          setTimeout(() => {
            setCollectionNameIsInputFocused(false);
          }, 50);
        }}
        onChangeText={setCollectionName}
        placeholder={t("screens.collections.addCollectionName")}
        placeholderTextColor={theme.colors.primary}
        outlineStyle={[styles.roundedOutline, { borderColor: theme.colors.primary }]}
        style={[styles.nameInput, { backgroundColor: "transparent" }]} />
      <CustomButton
        disabled={!collectionName.trim() || orTags.length === 0 || isSaving || collectionName.length === 0} 
        variant={ButtonVariant.NORMAL_PRIMARY}
        onPress={saveEditedCollection}
        style={styles.saveButton}
      >
        {t("screens.collections.save")}
      </CustomButton>
    </View>;
    if (Platform.OS === 'web') {
      return children;
    }
    return <KeyboardAvoidingView
      style={[gStyles.fullWidthWithMargin, gStyles.centerColumn]}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      keyboardVerticalOffset={Platform.OS === "ios" ? 80 : 0}
    >
      {children}
    </KeyboardAvoidingView>;
  }

  const rootStyle =
    Platform.OS === "web"
      ? [styles.safeArea, styles.webRoot]
      : [styles.safeArea, gStyles.fullWidth];

  return (
    <View style={rootStyle}>
      <ScrollView
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={[scrollContentStyle, {paddingBottom : keyboardHeight > 0 ? keyboardHeight + 120 : 150}]}
      >
        <View style={{gap : 10}}>
        <TagInputField selectedTags={orTags} onChangeTags={setOrTags} onFocusChange={setIsInputFocused}/>
        <View style={{flexDirection : "row", justifyContent : "space-between", alignItems: "center"}}>

        <Text numberOfLines={1} style={{ color: theme.colors.primary, fontSize: 16, includeFontPadding: true, textAlignVertical: "center" }}>
              {t("screens.collections.recommendation")}
        </Text>
        <Text numberOfLines={1} onPress={() => navigation.navigate(ROUTES.RECOMMENDED_COLLECTIONS, { origin: routeNav.name === ROUTES.EDIT_COLLECTION ? ROUTES.EDIT_COLLECTION : ROUTES.CREATE_COLLECTION,})} style={{ color: theme.colors.primary, fontWeight: "700", fontSize: 16,  includeFontPadding: true, textAlignVertical: "center" }}>
              {t("screens.collections.exploreMore")}
        </Text>
        </View>

        {recommendedCollections.length > 0 && (
          <LinearGradient
            colors={[theme?.colors?.appBackground2Web, theme?.colors?.appBackground3Web]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={[styles.circlesSection, { borderColor: theme.colors.primary }]}
          >
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.recommendationRow}>
              {recommendedBubbles}

            </ScrollView>
          </LinearGradient>
        )}
        </View>
        {renderOptionalFilters()}

        {isProcessing && (
          <View style={styles.processingWrap}>
            <View style={styles.processingInner}>
              <ThinkOLogo size={108} />
              <Text style={[gStyles.body, { color: theme.colors.primary, textAlign: "center", marginTop: 6 }]}>
                {t("screens.collections.processingMessage")}
              </Text>
            </View>
          </View>
        )}

        {editCollectionView()}

        {articles.length > 0 && <View style={styles.gridContainer}>{articleCards}</View>}
      </ScrollView>
    </View>
  );
};

export default EditCollectionScreen;

const styles = StyleSheet.create({
  safeArea: { flex: 1 },
  webRoot: { flex: 1, width: MODAL_MAX_WIDTH, maxWidth: "100%", alignSelf: "center"},
  container: { paddingHorizontal: 20, paddingVertical: 10 },
  pickTopicsSubtitle: { textAlign: "center", marginBottom: 8, fontStyle: "italic", fontSize: 14},
  recommendationRow: { flexDirection: "row", alignItems: "flex-start", paddingBottom: 4, paddingTop : 10},
  seeAllItem: { marginRight: 10, alignItems: "center"},
  seeAllCircle: { width: 70, height: 70, borderRadius: 35, justifyContent: "center", alignItems: "center"},
  seeAllLabel: { marginTop: 6, fontSize: 12, fontWeight: "500", width: 70, textAlign: "center"},
  circlesSection: { borderWidth: 1, borderRadius: 12, padding: 2, marginBottom: 15, shadowColor: "#000", shadowOpacity: 0.05, shadowRadius: 6, elevation: 2,paddingLeft : 15 },
  processingWrap: { marginTop: 8, marginBottom: 24 },
  processingInner: { alignItems: "center", justifyContent: "center", gap: 16 },
  gridItem: { width: "49%", marginBottom: 12 },
  gridContainer: { flexDirection: "row", flexWrap: "wrap", justifyContent: "space-between", marginTop: 25 },
  bottomRow: { flexDirection: "row", alignItems: "center", gap: 10, marginTop: 15 },
  nameInput: { borderRadius: 28, height: 42, fontSize: 12, flex: 1 },
  roundedOutline: { borderRadius: 12, borderWidth: 1 },
  saveButton: { minWidth: 90 },
  optionalFilterTitle: {
    fontSize: 16,
    fontWeight: "500",
    lineHeight: 20,
    marginBottom: 8,
  },
  optionalFilterGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  optionalChip: {
    flexBasis: "47%",
    flexGrow: 1,
    paddingVertical : 6,
    borderRadius: 25,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  optionalChipSelected: {
    borderWidth: 0,
  },
  optionalChipText: {
    fontSize: 14,
    fontWeight: "500",
    lineHeight: 24,
  },
});
